import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  X,
  Search,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  Wrench,
  Info,
} from "lucide-react";
import { useGlobalToast } from "@/contexts/ToastContext";

/**
 * Visor a pantalla completa del "cerebro" de Carmen (superadmin, sep-2026).
 * SOLO LECTURA: ni edita el prompt base (se cambia en código) ni el de
 * ficha (se edita en la configuración del negocio). El único bloque
 * editable desde aquí es "manual" (Instrucciones adicionales), vía el
 * enlace `onEditarManual` que reutiliza el editor YA existente en
 * `VoiceAiAdminDetail.tsx` — este componente nunca guarda nada por sí
 * mismo.
 */

export type OrigenBloque = "codigo" | "ficha" | "manual" | "llamada";

export interface PromptViewerBloque {
  id: string;
  titulo: string;
  origen: OrigenBloque;
  texto: string;
  chars: number;
}

export interface PromptViewerPromptData {
  texto: string;
  chars: number;
  tokensAprox: number;
  bloques: PromptViewerBloque[];
  /** Solo presente en el prompt "en vivo" de la sede, no en el de una llamada ya ocurrida. */
  herramientas?: string[];
  /** Solo tiene sentido con escenario "conocido": no había cliente real reciente, se usó uno ficticio. */
  clienteDeEjemplo?: boolean;
}

export type Escenario = "conocido" | "desconocido";

interface PromptViewerProps {
  titulo: string;
  subtitulo?: string;
  data: PromptViewerPromptData | null;
  loading: boolean;
  error: string | null;
  /** Si se pasa, se muestra el selector "Cliente desconocido / conocido" (solo en el prompt en vivo de la sede). */
  escenario?: Escenario;
  onCambiarEscenario?: (escenario: Escenario) => void;
  /** Si se pasa, los bloques de origen "manual" muestran un enlace que lleva al editor. */
  onEditarManual?: () => void;
  onClose: () => void;
}

const ORIGEN_CONFIG: Record<
  OrigenBloque,
  { etiqueta: string; chip: string; nota: string }
> = {
  codigo: {
    etiqueta: "Código",
    chip: "bg-gray-100 text-gray-700",
    nota: "Regla del sistema: se cambia en código.",
  },
  ficha: {
    etiqueta: "Ficha",
    chip: "bg-blue-100 text-blue-700",
    nota: "Se edita en la configuración del negocio.",
  },
  manual: {
    etiqueta: "Manual",
    chip: "bg-amber-100 text-amber-700",
    nota: "Escrito a mano por el superadmin en Instrucciones adicionales.",
  },
  llamada: {
    etiqueta: "Llamada",
    chip: "bg-green-100 text-green-700",
    nota: "Contexto propio de esta llamada (fecha, cliente identificado…).",
  },
};

/** Herramientas VAPI que solo existen en modo citas (ver `service.ts#buildVapiTools`) — sirve para deducir el modo del negocio sin tocar el backend. */
const HERRAMIENTAS_CITAS = new Set([
  "check_appointment_services",
  "check_appointment_slots",
  "create_appointment",
  "check_appointment",
  "modify_appointment",
  "cancel_appointment",
]);

export function inferirModoDesdeHerramientas(
  herramientas: string[] | undefined,
): "mesas" | "citas" | null {
  if (!herramientas || herramientas.length === 0) return null;
  return herramientas.some((h) => HERRAMIENTAS_CITAS.has(h))
    ? "citas"
    : "mesas";
}

function normalizar(s: string): string {
  return s.toLowerCase();
}

function resumenPorOrigen(bloques: PromptViewerBloque[]): string {
  const conteo: Record<OrigenBloque, number> = {
    codigo: 0,
    ficha: 0,
    manual: 0,
    llamada: 0,
  };
  for (const b of bloques) conteo[b.origen]++;
  const partes: string[] = [];
  if (conteo.codigo) partes.push(`${conteo.codigo} del código`);
  if (conteo.ficha) partes.push(`${conteo.ficha} de la ficha`);
  if (conteo.manual)
    partes.push(`${conteo.manual} manual${conteo.manual > 1 ? "es" : ""}`);
  if (conteo.llamada) partes.push(`${conteo.llamada} de la llamada`);
  const total = bloques.length;
  return `${total} bloque${total === 1 ? "" : "s"}${partes.length ? ": " + partes.join(" · ") : ""}`;
}

function resaltarCoincidencias(texto: string, query: string): ReactNode {
  const q = query.trim();
  if (!q) return texto;
  const escapado = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const partes = texto.split(new RegExp(`(${escapado})`, "gi"));
  const qLower = q.toLowerCase();
  return partes.map((parte, i) =>
    parte.toLowerCase() === qLower ? (
      <mark key={i} className="bg-yellow-200 text-gray-900 rounded-sm">
        {parte}
      </mark>
    ) : (
      <span key={i}>{parte}</span>
    ),
  );
}

export default function PromptViewer({
  titulo,
  subtitulo,
  data,
  loading,
  error,
  escenario,
  onCambiarEscenario,
  onEditarManual,
  onClose,
}: PromptViewerProps) {
  const { showToast } = useGlobalToast();
  const [query, setQuery] = useState("");
  const [indiceAbiertoMovil, setIndiceAbiertoMovil] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const bloqueRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Al cambiar de prompt (otra sede, otro escenario, otra llamada) se limpia
  // la búsqueda: si no, un filtro de la llamada anterior podría dejar el
  // visor nuevo vacío sin que se note por qué.
  useEffect(() => {
    setQuery("");
  }, [data]);

  const bloquesVisibles = useMemo(() => {
    if (!data) return [];
    const q = normalizar(query);
    if (!q) return data.bloques;
    return data.bloques.filter(
      (b) => normalizar(b.titulo).includes(q) || normalizar(b.texto).includes(q),
    );
  }, [data, query]);

  const handleCopiarTodo = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.texto);
      setCopiado(true);
      showToast("Prompt copiado al portapapeles", "success");
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      showToast("No se pudo copiar. Selecciónalo a mano.", "error");
    }
  };

  const irAlBloque = (id: string) => {
    bloqueRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (window.matchMedia("(max-width: 767px)").matches) {
      setIndiceAbiertoMovil(false);
    }
  };

  const renderIndice = () => (
    <ul className="divide-y divide-gray-100">
      {bloquesVisibles.map((b) => {
        return (
          <li key={b.id}>
            <button
              onClick={() => irAlBloque(b.id)}
              className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50"
            >
              <span className="flex items-center justify-between gap-2">
                <span className="truncate font-medium text-gray-800">
                  {b.titulo}
                </span>
                <span
                  className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${ORIGEN_CONFIG[b.origen].chip}`}
                >
                  {ORIGEN_CONFIG[b.origen].etiqueta}
                </span>
              </span>
              <span className="text-[10px] text-gray-400">
                {b.chars.toLocaleString("es-ES")} caracteres
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Cabecera */}
      <div className="flex-shrink-0 border-b border-gray-200 px-3 py-3 md:px-6 md:py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base md:text-lg font-bold text-gray-900 truncate">
              {titulo}
            </h2>
            {subtitulo && (
              <p className="text-xs md:text-sm text-gray-500 mt-0.5">
                {subtitulo}
              </p>
            )}
            {data && (
              <p className="text-xs md:text-sm text-gray-600 mt-1">
                {data.chars.toLocaleString("es-ES")} caracteres · ~
                {data.tokensAprox.toLocaleString("es-ES")} tokens por llamada
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {onCambiarEscenario && escenario && (
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-xs">
              <button
                onClick={() => onCambiarEscenario("desconocido")}
                className={`px-2.5 py-1.5 ${
                  escenario === "desconocido"
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                Cliente desconocido
              </button>
              <button
                onClick={() => onCambiarEscenario("conocido")}
                className={`px-2.5 py-1.5 border-l border-gray-300 ${
                  escenario === "conocido"
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                Cliente conocido
              </button>
            </div>
          )}

          <div className="relative flex-1 min-w-[160px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar en el prompt…"
              className="w-full pl-7 pr-7 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Limpiar búsqueda"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <button
            onClick={handleCopiarTodo}
            disabled={!data}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            {copiado ? (
              <Check className="w-3.5 h-3.5 text-green-600" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
            {copiado ? "Copiado" : "Copiar todo"}
          </button>
        </div>

        {data?.clienteDeEjemplo && escenario === "conocido" && (
          <p className="mt-2 inline-flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            No hay una llamada reciente de un cliente real en esta sede: se
            está usando un cliente ficticio de ejemplo.
          </p>
        )}

        {data && data.herramientas && data.herramientas.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
              <Wrench className="w-3 h-3" /> Herramientas cargadas:
            </span>
            {data.herramientas.map((h) => (
              <span
                key={h}
                className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] font-mono"
              >
                {h}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Cuerpo: índice + bloques */}
      <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
        {/* Índice — plegable en móvil, fijo en desktop */}
        {data && data.bloques.length > 0 && (
          <>
            <div className="md:hidden flex-shrink-0 border-b border-gray-200">
              <button
                onClick={() => setIndiceAbiertoMovil((s) => !s)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50"
              >
                <span>Índice · {resumenPorOrigen(data.bloques)}</span>
                {indiceAbiertoMovil ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              {indiceAbiertoMovil && (
                <div className="max-h-56 overflow-y-auto">{renderIndice()}</div>
              )}
            </div>

            <div className="hidden md:flex md:w-72 flex-shrink-0 border-r border-gray-200 flex-col overflow-hidden">
              <p className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100 flex-shrink-0">
                {resumenPorOrigen(data.bloques)}
              </p>
              <div className="flex-1 overflow-y-auto">{renderIndice()}</div>
            </div>
          </>
        )}

        {/* Contenido */}
        <div className="flex-1 overflow-y-auto px-3 py-4 md:px-6 md:py-6">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          )}

          {!loading && error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          {!loading && !error && data && bloquesVisibles.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-16">
              Ningún bloque contiene "{query}".
            </p>
          )}

          {!loading &&
            !error &&
            data &&
            bloquesVisibles.map((b) => (
              <div
                key={b.id}
                ref={(el) => {
                  bloqueRefs.current[b.id] = el;
                }}
                className="mb-6 scroll-mt-3"
              >
                <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-900">
                      {b.titulo}
                    </h3>
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${ORIGEN_CONFIG[b.origen].chip}`}
                    >
                      {ORIGEN_CONFIG[b.origen].etiqueta}
                    </span>
                    <span className="text-[10px] text-gray-400">
                      {b.chars.toLocaleString("es-ES")} caracteres
                    </span>
                  </div>
                  {b.origen === "manual" && onEditarManual ? (
                    <button
                      onClick={onEditarManual}
                      className="text-xs text-amber-700 hover:text-amber-900 underline underline-offset-2"
                    >
                      Editar en Instrucciones adicionales
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">
                      {ORIGEN_CONFIG[b.origen].nota}
                    </span>
                  )}
                </div>
                <pre className="whitespace-pre-wrap break-words font-mono text-[11px] md:text-xs text-gray-800 leading-relaxed bg-gray-50 border border-gray-200 rounded-lg p-3">
                  {resaltarCoincidencias(b.texto, query)}
                </pre>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
